import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { Drawer, Tabs, Row, Col, Button, Modal, message } from 'antd'
import { ResourceProfileInfo } from '@mercadoni/gungnir'
import { observer } from 'mobx-react-lite'

import { useResourceProfileStore } from './resource-profile.context'

import StatusTab from './status-tab.component'
import AssignedQuotasTab from './assigned-quotas-tab.component'
import HistoricTab from './historic-tab.component'

import './resource-profile.styles.less'
import { InfoCircleOutlined } from '@ant-design/icons'
import moment from 'moment'
import { useGate } from 'statsig-react'
import { bringResource, reactivateResourceById } from '../resource-management/resource-management.services'
import { ACTIVITY_LOG_EVENT_TYPES } from '../../utils/constants'
import { Can } from '../../refactory/modules/access-control/infrastructure/roles-permissions/access-control'
import { SubModulePerms } from '../../refactory/modules/access-control/domain/module'
import { getFormattedDate, TIME_FORMAT } from '../../refactory/utils/dates'

const ResourceProfile = (props) => {
  const { resourceId, visible, onChangeVisible, showCustomTooltip, bodyCustomTooltip, clientTimeZone } = props
  const { t } = useTranslation()
  const store = useResourceProfileStore()
  const [activeTab, setActiveTab] = useState('1')
  const [scrollY, setScrollY] = useState(0)
  const isRestoreResourceActive = useGate('is_restore_resource_active').value
  const { confirm } = Modal

  const getResourceProfileInfoProps = () => {
    if (store.resourceInfo && !store.resourceInfo.loading) {
      const {
        created_at,
        email,
        last_update,
        first_name,
        last_name,
        phone_number,
        photo_url,
        vehicle,
        wants_to_receive_tasks
      } = store.resourceInfo
      return {
        createdAt: t('createdAt', { date: dayjs(created_at).format('DD/MM/YY') }),
        email,
        lastUpdatePosition: last_update
          ? t('lastUpdatePosition', {
              date: getFormattedDate({ date: last_update, format: TIME_FORMAT, timezoneIn: clientTimeZone })
            })
          : t('without-last-location'),
        name: `${first_name} ${last_name}`,
        phoneNumber: phone_number,
        photoUrl: photo_url,
        shopperAppVersion: t('noAvailable'),
        vehicleType: vehicle ? vehicle.label : '',
        wantsToReceiveTasks: wants_to_receive_tasks,
        loading: false
      }
    }
    return { loading: true }
  }

  useEffect(() => {
    const initialize = () => {
      if (visible && resourceId) {
        store.loadResourceInfo(resourceId)
        store.loadAssignedTasks(resourceId, 5, 0)
      }
    }

    if (visible) {
      initialize()
    }
    return () => {
      store.cleanAll()
      setActiveTab('1')
    }
  }, [resourceId, store, visible])

  const onHandleReactivateResource = async (resource) => {
    const eventType = ACTIVITY_LOG_EVENT_TYPES.POST_RESTORE_RESOURCES
    let response = await reactivateResourceById(resource.id, eventType)
    if (response) {
      message.success(
        `${t('resources.resource')} ${resource.first_name} ${resource.last_name} ${t('resources.restore')}`
      )
    }
    props.getResources(props.pagination, props.query, props.resourceStates)
  }

  const showModalToReactivateResource = async (resource) => {
    const temporalResource = await bringResource(resource.id)

    confirm({
      title: t('resources.capacity.reactivateResourceTitle'),
      width: 500,
      content: t('resources.capacity.reactivateText'),
      okText: t('resources.capacity.restore'),
      onOk: () => {
        onHandleReactivateResource(temporalResource)
      },
      cancelText: t('resources.capacity.cancel')
    })
  }

  return (
    <>
      {visible && (
        <Drawer
          onScroll={({ target: { scrollTop } }) => {
            setScrollY(scrollTop)
          }}
          key='resource-profile-1'
          title={t('resourceProfile')}
          placement='right'
          width={640}
          closable
          onClose={() => onChangeVisible(false)}
          visible={visible}
        >
          <div className='resource-profile-drawer'>
            {isRestoreResourceActive && Can([SubModulePerms.RES_RESOURCE_SWITCH]) ? (
              store.resourceInfo.state === 'DELETED' ? (
                <Row className='div-banner-restore-resource'>
                  <Col span={2} className='div-icon-banner-restore-resource'>
                    <InfoCircleOutlined className='icon-banner-restore-resource' />
                  </Col>
                  <Col span={16}>
                    <label className='label-title-banner-restore-resource'>{t('resources.capacity.userDeleted')}</label>
                    <label className='label-date-banner-restore-resource'>
                      {t('resources.capacity.actionDate')} {moment(store.resourceInfo.last_update).format('DD/MM/YYYY')}
                    </label>
                  </Col>
                  <Col span={6} className='div-button-banner-restore-resource'>
                    <Button onClick={() => showModalToReactivateResource(store.resourceInfo)} type='primary'>
                      {t('resources.capacity.restoreOkay')}
                    </Button>
                  </Col>
                </Row>
              ) : null
            ) : null}
            <ResourceProfileInfo {...getResourceProfileInfoProps()} />
            <Tabs activeKey={activeTab} onChange={(activeKey) => setActiveTab(activeKey)}>
              <Tabs.TabPane tab={t('historic')} key='1' disabled={store.resourceInfo.state === 'DELETED'}>
                <HistoricTab {...props} activeTab={activeTab} scrollY={scrollY} />
              </Tabs.TabPane>
              <Tabs.TabPane tab={t('assignedQuotas')} key='2' disabled={store.resourceInfo.state === 'DELETED'}>
                <AssignedQuotasTab
                  {...props}
                  activeTab={activeTab}
                  showCustomTooltip={showCustomTooltip}
                  bodyCustomTooltip={bodyCustomTooltip}
                />
              </Tabs.TabPane>
              <Tabs.TabPane tab={t('status')} key='3'>
                <StatusTab {...props} resourceInfo={store.resourceInfo} />
              </Tabs.TabPane>
            </Tabs>
          </div>
        </Drawer>
      )}
    </>
  )
}

export default observer(ResourceProfile)
